import { TrackToolService } from '@analytics/events';
import type { ToolTrackValues } from '@analytics/models';
import { AnalyticsActionTypes } from '@analytics/models';
import {
  analyticsHash,
  analyticsRoundNumericValue,
  lowerCaseObjectKeys,
} from '@analytics/utils';
import { Injectable, Injector } from '@angular/core';
import type { Offer } from '@innogy/become-a-customer/shared/interfaces';
import type { StoreProductData } from '@legacy/common-ui-models';

import { mapProductToCommerce } from '../../helpers';
import { mapOfferToCommerce } from '../../helpers/map-offer-to-commerce';
import type {
  OrderFormLaststepExtraValues,
  OrderFormLaststepResult,
} from './order-form-laststep.model';

@Injectable({
  providedIn: 'root',
})
export class OrderFormLaststepService {
  public readonly trackOrderLaststep = (
    orderFlowResultValues: OrderFormLaststepResult,
    offer: Offer
  ) => {
    const trackingValues: ToolTrackValues = {
      step: this.determineStep(orderFlowResultValues),
      stepName: '',
      type: AnalyticsActionTypes.LAST_STEP,
      rendering: {
        componentName: 'order',
      },
    };

    const {
      transactieId,
      leadId,
      testAanmelding,
      stroomverbruikJaar,
      gasverbruikJaar,
      typeKlant,
      isKlant,
      aantalPersonen,
      typeAansluiting,
      typeMeter,
      zonnepanelen,
      prijs,
      vatIncluded,
      customerEmailAddress,
      crossSellProduct,
      thuisvoordeelAanmelding,
    } = orderFlowResultValues;

    const extraValues: OrderFormLaststepExtraValues = {
      result: {
        transactieId,
        omzet: analyticsRoundNumericValue(vatIncluded ? prijs : prijs * 1.21),
        leadId,
        testAanmelding,
        stroomverbruikJaar,
        gasverbruikJaar,
        typeKlant,
        isKlant,
        aantalPersonen,
        typeAansluiting,
        typeMeter,
        zonnepanelen,
        thuisvoordeelAanmelding,
      },
      commerce: mapOfferToCommerce(offer),
      crossSellCommerce: this.mapCrossSellProduct(crossSellProduct),
      emailaddressHash: customerEmailAddress
        ? analyticsHash(customerEmailAddress)
        : undefined,
    };

    /**
     * WTA-17: resolve injector context
     */
    const trackToolService = this.injector.get(TrackToolService);

    return trackToolService.trackTool(trackingValues, {
      ...extraValues,
      result: lowerCaseObjectKeys(extraValues.result),
    });
  };

  constructor(private readonly injector: Injector) {}

  /**
   * Utilities
   */

  /**
   * The step id of the last step depends on wether a cross sell product is shown during the order
   */
  determineStep(orderValues: OrderFormLaststepResult) {
    return orderValues.crossSellProduct ? 6 : 5;
  }

  /**
   * Maps an eplus product to a commerce object or return undefined if this is not possible
   */
  mapCrossSellProduct(product: StoreProductData | undefined) {
    if (product) {
      try {
        return mapProductToCommerce(product);
      } catch {
        return undefined;
      }
    }
    return undefined;
  }
}
